import { Button, Input, Select, toast } from "@gotts.ai/ui";
import { ArrowRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { encodeFunctionData, formatEther, formatUnits, parseUnits } from "viem";

import { MaxButton } from "../../components/MaxButton";
import { useDeployment, useEvmBackend } from "../../context";
import { useRpcRequest } from "../../hooks/useRpcRequest";
import { MOCK_ERC20_ABI, WETH9_ABI } from "../../lib/abis";
import { formatDisplayAmount } from "../../lib/format";

import type { ChainControlSharedProps } from "./types";
import type { Address, Hex } from "viem";

export function WrapUnwrapCard({
  loading,
  withLoading,
  onAction,
  blockNumber,
}: ChainControlSharedProps) {
  const { request } = useRpcRequest();
  const { deployment } = useDeployment();
  const { accounts } = useEvmBackend();

  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        label: `${a.label} (${a.address.slice(0, 6)}...${a.address.slice(-4)})`,
        value: a.address,
      })),
    [accounts],
  );

  const [account, setAccount] = useState<string>(accounts[0]?.address ?? "");
  const [wrapAmount, setWrapAmount] = useState("");
  const [unwrapAmount, setUnwrapAmount] = useState("");
  const [ethBalance, setEthBalance] = useState<string | null>(null);
  const [wethBalance, setWethBalance] = useState<string | null>(null);

  const wethAddress = deployment?.tokens?.weth;

  const fetchBalances = useCallback(async () => {
    if (!account) return;
    try {
      const hex = (await request("eth_getBalance", [
        account,
        "latest",
      ])) as string;
      setEthBalance(formatEther(BigInt(hex)));
    } catch {
      setEthBalance(null);
    }

    if (!wethAddress) {
      setWethBalance(null);
      return;
    }
    try {
      const calldata = encodeFunctionData({
        abi: MOCK_ERC20_ABI,
        functionName: "balanceOf",
        args: [account as Address],
      });
      const raw = (await request("eth_call", [
        { to: wethAddress, data: calldata },
        "latest",
      ])) as string | null;
      if (raw && raw !== "0x") {
        setWethBalance(formatUnits(BigInt(raw), 18));
      } else {
        setWethBalance("0");
      }
    } catch {
      setWethBalance(null);
    }
  }, [account, wethAddress, request]);

  useEffect(() => {
    void fetchBalances();
  }, [fetchBalances, blockNumber]);

  async function handleWrap() {
    if (!wrapAmount || !wethAddress) return;

    await withLoading("wrap", async () => {
      const weiAmount = parseUnits(wrapAmount, 18);
      const weiHex = ("0x" + weiAmount.toString(16)) as Hex;

      const calldata = encodeFunctionData({
        abi: WETH9_ABI,
        functionName: "deposit",
      });

      await request("anvil_impersonateAccount", [account]);
      await request("eth_sendTransaction", [
        {
          from: account,
          to: wethAddress,
          data: calldata,
          value: weiHex,
        },
      ]);
      await request("anvil_stopImpersonatingAccount", [account]);

      toast.success(`Wrapped ${wrapAmount} ETH \u2192 WETH`);
      onAction?.(`Wrapped ${wrapAmount} ETH`);
      setWrapAmount("");
    });
  }

  async function handleUnwrap() {
    if (!unwrapAmount || !wethAddress) return;

    await withLoading("unwrap", async () => {
      const weiAmount = parseUnits(unwrapAmount, 18);

      const calldata = encodeFunctionData({
        abi: WETH9_ABI,
        functionName: "withdraw",
        args: [weiAmount],
      });

      await request("anvil_impersonateAccount", [account]);
      await request("eth_sendTransaction", [
        {
          from: account,
          to: wethAddress,
          data: calldata,
        },
      ]);
      await request("anvil_stopImpersonatingAccount", [account]);

      toast.success(`Unwrapped ${unwrapAmount} WETH \u2192 ETH`);
      onAction?.(`Unwrapped ${unwrapAmount} WETH`);
      setUnwrapAmount("");
    });
  }

  return (
    <div className="space-y-4">
      <Select
        options={accountOptions}
        value={account}
        onValueChange={setAccount}
        placeholder="Select account"
      />

      {/* Balance overview */}
      <div className="flex items-center justify-between rounded-sm bg-[rgba(255,255,255,0.02)] px-4 py-3">
        <div className="text-center">
          <p className="hero-value text-lg">
            {ethBalance !== null
              ? formatDisplayAmount(ethBalance).display
              : "--"}
          </p>
          <p className="font-mono text-[10px] text-[var(--color-text-muted)]">
            ETH
          </p>
        </div>
        <div className="flex flex-col items-center gap-0.5 px-4">
          <ArrowRight size={14} className="text-[var(--color-gold)]/40" />
          <span className="font-mono text-[8px] text-[var(--color-text-muted)]">
            1:1
          </span>
        </div>
        <div className="text-center">
          <p className="hero-value text-lg">
            {wethBalance !== null
              ? formatDisplayAmount(wethBalance).display
              : "--"}
          </p>
          <p className="font-mono text-[10px] text-[var(--color-text-muted)]">
            WETH
          </p>
        </div>
      </div>

      {/* Wrap / Unwrap — two columns */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Wrap */}
        <div className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            Wrap ETH
          </p>
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              placeholder="ETH amount"
              value={wrapAmount}
              onChange={(e) => {
                setWrapAmount(e.target.value);
              }}
              className="flex-1"
            />
            {ethBalance !== null && (
              <MaxButton
                onClick={() => {
                  const maxWrap = Math.max(0, parseFloat(ethBalance) - 0.01);
                  setWrapAmount(String(maxWrap > 0 ? maxWrap : 0));
                }}
                disabled={loading !== null}
                title={
                  ethBalance
                    ? `Max: ${formatDisplayAmount(ethBalance).display} ETH`
                    : undefined
                }
              />
            )}
          </div>
          <Button
            variant="gold"
            size="sm"
            onClick={handleWrap}
            loading={loading === "wrap"}
            disabled={loading !== null || !wrapAmount || !wethAddress}
            className="w-full"
          >
            Wrap {wrapAmount ? `${wrapAmount} ETH` : ""}
          </Button>
        </div>

        {/* Unwrap */}
        <div className="space-y-2">
          <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
            Unwrap WETH
          </p>
          <div className="flex items-center gap-1.5">
            <Input
              type="number"
              placeholder="WETH amount"
              value={unwrapAmount}
              onChange={(e) => {
                setUnwrapAmount(e.target.value);
              }}
              className="flex-1"
            />
            {wethBalance !== null && (
              <MaxButton
                onClick={() => {
                  setUnwrapAmount(wethBalance);
                }}
                disabled={loading !== null}
                title={
                  wethBalance
                    ? `Max: ${formatDisplayAmount(wethBalance).display} WETH`
                    : undefined
                }
              />
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUnwrap}
            loading={loading === "unwrap"}
            disabled={loading !== null || !unwrapAmount || !wethAddress}
            className="w-full"
          >
            Unwrap {unwrapAmount ? `${unwrapAmount} WETH` : ""}
          </Button>
        </div>
      </div>
    </div>
  );
}
